import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { fileToCompressedDataUrl } from '../../utils/image';

const ALL_ROLES = { admin_general: 'Admin General', admin: 'Administrador', barbero: 'Barbero' };
// Roles que cada nivel puede asignar al crear un usuario
const ALLOWED_ROLES = {
  admin_general: { admin: 'Administrador', barbero: 'Barbero' },
  admin:         { barbero: 'Barbero' },
};
const EMPTY = { name: '', cedula: '', phone: '', email: '', role: 'admin', also_barber: false, password: 'barbero123', photo: '', branch_id: '' };

// Helper para campos que el backend puede devolver en español o inglés
const wName    = w => w.nombre   || w.name   || '';
const wPhone   = w => w.telefono || w.phone  || '';
const wActive  = w => w.activo   !== undefined ? w.activo : (w.is_active !== undefined ? w.is_active : true);

export default function WorkersPage() {
  const [workers, setWorkers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [maxWorkers, setMaxWorkers] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [branches, setBranches] = useState([]);

  const load = () => {
    setLoading(true);
    api.get('/workers')
      .then(r => setWorkers(r.data))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/auth/me').then(r => {
      setUserRole(r.data.role || '');
      setMaxWorkers(r.data.max_usuarios ?? null);
    }).catch(() => {});
    api.get('/branches').then(r => setBranches(r.data.filter(b => b.activo))).catch(() => {});
  }, []);

  const openCreate = () => { setForm(EMPTY); setModal('create'); setError(''); };
  const openEdit   = w => {
    setForm({
      name:       wName(w),
      cedula:     w.cedula || '',
      phone:      wPhone(w),
      email:      w.email || '',
      role:       w.role || 'admin',
      also_barber: w.also_barber || false,
      password:   '',
      photo:      w.url_foto || '',
      branch_id:  w.sucursal_id ? String(w.sucursal_id) : '',
    });
    setModal(w);
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (modal === 'create') await api.post('/workers', form);
      else await api.put(`/workers/${modal.id}`, form);
      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const toggle = async id => {
    try {
      await api.patch(`/workers/${id}/toggle`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cambiar estado');
    }
  };

  const activeCount = workers.filter(w => wActive(w) && ['barbero','admin','admin_general'].includes(w.role)).length;
  const canManage = userRole === 'admin' || userRole === 'admin_general';
  const atLimit = maxWorkers !== null && activeCount >= maxWorkers;

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Trabajadores</h2>
          <p>Gestiona el equipo de tu barbería</p>
        </div>
        {canManage && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            {maxWorkers !== null && (
              <span style={{ fontSize: 13, color: atLimit ? 'var(--color-danger, #ef4444)' : 'var(--color-text-muted)' }}>
                {activeCount} de {maxWorkers} trabajadores activos
              </span>
            )}
            <button
              className="btn btn-primary"
              onClick={openCreate}
              disabled={atLimit}
              title={atLimit ? `Límite del plan alcanzado (${maxWorkers} trabajadores)` : ''}
            >
              + Nuevo Trabajador
            </button>
            {atLimit && (
              <span style={{ fontSize: 12, color: 'var(--color-danger, #ef4444)' }}>
                Límite del plan alcanzado
              </span>
            )}
          </div>
        )}
      </div>

      {error && !modal && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : workers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">👥</div>
          <h3>Sin trabajadores</h3>
          <p>Agrega tu primer trabajador para comenzar</p>
          {canManage && <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openCreate}>+ Agregar</button>}
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Nombre</th>
                  <th>Cédula</th>
                  <th>Celular</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Puede atender</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {workers.map(w => {
                  const active    = wActive(w);
                  const canServe  = (w.role === 'barbero' || w.also_barber) && active;
                  return (
                    <tr key={w.id}>
                      <td style={{ width: 44 }}>
                        {w.url_foto
                          ? <img src={w.url_foto} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
                          : <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--color-surface-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>✂️</div>
                        }
                      </td>
                      <td style={{ fontWeight: 500 }}>{wName(w)}</td>
                      <td>{w.cedula || '—'}</td>
                      <td>{wPhone(w) || '—'}</td>
                      <td style={{ color: 'var(--color-text-muted)' }}>{w.email}</td>
                      <td><span className="badge badge-gold">{ALL_ROLES[w.role] || w.role}</span></td>
                      <td style={{ textAlign: 'center', fontSize: 18 }}>{canServe ? '✓' : '—'}</td>
                      <td><span className={`badge badge-${active ? 'active' : 'inactive'}`}>{active ? 'Activo' : 'Inactivo'}</span></td>
                      <td>
                        {canManage && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-sm btn-secondary" onClick={() => openEdit(w)}>✏️ Editar</button>
                            <button className={`btn btn-sm ${active ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggle(w.id)}>
                              {active ? 'Desactivar' : 'Activar'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'Nuevo Trabajador' : 'Editar Trabajador'}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}
                <div className="form-grid">
                  {/* Foto del trabajador */}
                  <div className="form-group" style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 16 }}>
                    {form.photo
                      ? <img src={form.photo} alt="" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--color-gold)' }} />
                      : <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--color-surface-raised)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30 }}>✂️</div>
                    }
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ display: 'inline-block', padding: '8px 16px', background: 'var(--color-surface-raised)', border: '1px solid var(--color-border)', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem' }}>
                        {form.photo ? 'Cambiar foto' : 'Subir foto'}
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async e => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (!file) return;
                          try {
                            // Comprimida antes de guardar (ver nota en Mi Negocio)
                            const dataUrl = await fileToCompressedDataUrl(file, 400, 0.85);
                            setForm(f => ({ ...f, photo: dataUrl }));
                          } catch (err) {
                            alert(err.message || 'No se pudo procesar la imagen');
                          }
                        }} />
                      </label>
                      {form.photo && (
                        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: '0.8rem', padding: 0, textAlign: 'left' }}
                          onClick={() => setForm(f => ({ ...f, photo: '' }))}>
                          Quitar foto
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Nombre completo *</label>
                    <input
                      className="form-input"
                      required
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cédula</label>
                    <input
                      className="form-input"
                      value={form.cedula || ''}
                      onChange={e => setForm(f => ({ ...f, cedula: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Celular</label>
                    <input
                      className="form-input"
                      value={form.phone || ''}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Email {form.role === 'barbero' ? '(usuario para ingresar) *' : '*'}
                    </label>
                    <input
                      type="email"
                      className="form-input"
                      required
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rol principal *</label>
                    <select
                      className="form-select"
                      value={form.role}
                      onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                    >
                      {Object.entries(ALLOWED_ROLES[userRole] || ALL_ROLES).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  {branches.length > 0 && (
                    <div className="form-group" style={{ gridColumn: '1/-1' }}>
                      <label className="form-label">Sucursal</label>
                      <select
                        className="form-select"
                        value={form.branch_id}
                        onChange={e => setForm(f => ({ ...f, branch_id: e.target.value }))}
                      >
                        <option value="">— Sin sucursal asignada —</option>
                        {branches.map(b => (
                          <option key={b.id} value={String(b.id)}>{b.nombre}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={form.also_barber || false}
                        onChange={e => setForm(f => ({ ...f, also_barber: e.target.checked }))}
                        style={{ width: 16, height: 16, accentColor: 'var(--color-primary)' }}
                      />
                      <span className="form-label" style={{ margin: 0 }}>
                        También puede atender citas como barbero
                      </span>
                    </label>
                    <span className="form-hint" style={{ marginTop: 4, display: 'block' }}>
                      Permite que este trabajador aparezca como barbero disponible en el sistema de citas.
                    </span>
                  </div>
                  {modal === 'create' && (
                    <div className="form-group" style={{ gridColumn: '1/-1' }}>
                      <label className="form-label">
                        Contraseña {form.role === 'barbero' ? '*' : '(opcional)'}
                      </label>
                      <input
                        className="form-input"
                        type="password"
                        required={form.role === 'barbero'}
                        minLength={form.role === 'barbero' ? 6 : undefined}
                        value={form.password}
                        onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                        placeholder={form.role === 'barbero' ? 'Mínimo 6 caracteres' : 'Dejar vacío para usar "barbero123"'}
                      />
                      <span className="form-hint">
                        {form.role === 'barbero'
                          ? 'El barbero usará este email y contraseña para iniciar sesión.'
                          : 'El usuario podrá cambiarla después.'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><div className="spinner" />Guardando...</> : '💾 Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
